import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from './api/client'
import { refreshPendingCount, syncPending } from './api/sync'
import { DashboardView } from './components/DashboardView'
import { PalletsView } from './components/PalletsView'
import { ReadingWorkflow } from './components/ReadingWorkflow'
import { SensorsView } from './components/SensorsView'
import { LoginView } from './components/LoginView'
import { AppBrand } from './components/AppBrand'
import { appStore, useAppState } from './state/appStore'
import { reconcilePendingCatalog } from './db/offlineDb'
import type { AuthUser, BootstrapData } from './types/domain'

type Page = 'dashboard' | 'pallets' | 'sensors' | 'reading'

export default function App() {
  const [page, setPage] = useState<Page>('dashboard')
  const [data, setData] = useState<BootstrapData>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [authUser, setAuthUser] = useState<AuthUser | null>()
  const app = useAppState()

  const reloadData = useCallback(async () => {
    try {
      setError(undefined)
      const nextData = await api.bootstrap()
      setData(nextData)
      await reconcilePendingCatalog(new Set(nextData.pallets.map(pallet => pallet.id)), new Set(nextData.sensors.map(sensor => sensor.id)))
      await refreshPendingCount()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'API недоступен')
      throw value
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true
    api.me().then(async user => {
      if (!active) return
      setAuthUser(user)
      await api.csrf()
      await reloadData()
    }).catch(value => {
      if (!active) return
      if (value instanceof ApiError && value.status === 401) {
        setAuthUser(null)
        setLoading(false)
      } else {
        setError(value instanceof Error ? value.message : 'API недоступен')
        setLoading(false)
      }
    })
    return () => { active = false }
  }, [reloadData])

  useEffect(() => {
    if (!authUser) return
    refreshPendingCount().catch(() => undefined)
    const online = () => syncPending().then(reloadData).catch(() => undefined)
    window.addEventListener('online', online)
    const timer = window.setInterval(() => refreshPendingCount().catch(() => undefined), 15_000)
    return () => { window.removeEventListener('online', online); window.clearInterval(timer) }
  }, [authUser, reloadData])

  useEffect(() => {
    const unauthorized = () => {
      setAuthUser(null)
      setData(undefined)
      setLoading(false)
      setError(undefined)
    }
    window.addEventListener('novaroll:unauthorized', unauthorized)
    return () => window.removeEventListener('novaroll:unauthorized', unauthorized)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [page])

  async function login(username: string, password: string) {
    const user = await api.login(username, password)
    setLoading(true)
    setError(undefined)
    setAuthUser(user)
    await reloadData()
  }

  async function logout() {
    try {
      await api.logout()
    } finally {
      setAuthUser(null)
      setData(undefined)
      setPage('dashboard')
      setLoading(false)
    }
  }

  async function manualSync() {
    try {
      const summary = await syncPending()
      await reloadData()
      if (summary.synced > 0) {
        appStore.notify('success', `Отправлено: ${summary.synced}${summary.conflicts ? ` · старых конфликтов: ${summary.conflicts}` : ''}`)
      } else if (summary.conflicts || summary.firstError) {
        appStore.notify('error', `Очередь требует внимания: ${summary.firstError ?? `${summary.conflicts} конфликт(а)`}`)
      } else {
        appStore.notify('success', `Синхронизация завершена · отправлено: ${summary.synced}`)
      }
    } catch (value) {
      appStore.notify('error', value instanceof Error ? value.message : 'Ошибка синхронизации')
    }
  }

  if (authUser === undefined) return <div className="login-loading"><AppBrand /><span>Проверка сессии…</span></div>
  if (authUser === null) return <LoginView onLogin={login} />

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <a className="brand" href="#top" aria-label="NOVAROLL — на главную">
          <AppBrand tagline />
        </a>
        <nav aria-label="Основная навигация">
          <NavButton active={page === 'dashboard'} onClick={() => setPage('dashboard')} icon="⌂">Обзор</NavButton>
          <NavButton active={page === 'pallets'} onClick={() => setPage('pallets')} icon="◇">Паллеты и графики</NavButton>
          <NavButton active={page === 'sensors'} onClick={() => setPage('sensors')} icon="◉">Датчики</NavButton>
          <NavButton active={page === 'reading'} onClick={() => setPage('reading')} icon="⌁" accent>Считать BLE</NavButton>
        </nav>
        <div className="sidebar-foot"><span className={`connection-dot ${app.online ? 'online' : ''}`} /><div><strong>{app.online ? 'Сервер доступен' : 'Offline-режим'}</strong><small>{app.online ? 'Синхронизация разрешена' : 'Данные сохраняются локально'}</small></div></div>
      </aside>
      <div className="main-column" id="top">
        <header className="topbar">
          <div className="mobile-brand"><AppBrand /></div>
          <div className="topbar-meta"><span>Контур</span><strong>Пилотная эксплуатация</strong></div>
          <div className="topbar-actions">
            <span className="user-pill" title="Текущий пользователь">{authUser.username}</span>
            <span className={`network-pill ${app.online ? 'online' : 'offline'}`}>{app.online ? 'Online' : 'Offline'}</span>
            <button className="sync-button" onClick={manualSync} disabled={app.syncing || !app.online} aria-label="Синхронизировать локальные операции">
              <span className={app.syncing ? 'spin' : ''} aria-hidden="true">↻</span>
              <span className="sync-label">{app.syncing ? 'Синхронизация' : `Очередь: ${app.pending}`}</span>
              <span className="sync-count" aria-hidden="true">{app.pending}</span>
            </button>
            <button className="logout-button" aria-label={`Выйти из учётной записи ${authUser.username}`} onClick={() => logout().catch(value => appStore.notify('error', value instanceof Error ? value.message : 'Ошибка выхода'))}>
              <LogoutIcon /><span className="logout-label">Выйти</span>
            </button>
          </div>
        </header>
        <main>
          {loading && <Loading />}
          {!loading && error && <ApiUnavailable message={error} retry={() => { setLoading(true); reloadData().catch(() => undefined) }} />}
          {!loading && data && page === 'dashboard' && <DashboardView data={data} onOpenPallets={() => setPage('pallets')} />}
          {!loading && data && page === 'pallets' && <PalletsView pallets={data.pallets} />}
          {!loading && data && page === 'sensors' && <SensorsView sensors={data.sensors} />}
          {!loading && data && page === 'reading' && <ReadingWorkflow pallets={data.pallets} sensors={data.sensors} onDataChanged={reloadData} />}
        </main>
      </div>
      {app.toast && <div className={`toast toast-${app.toast.kind}`} role="status">{app.toast.message}</div>}
      <nav className="mobile-nav" aria-label="Мобильная навигация">
        <MobileNavButton active={page === 'dashboard'} icon="⌂" onClick={() => setPage('dashboard')}>Обзор</MobileNavButton>
        <MobileNavButton active={page === 'pallets'} icon="◇" onClick={() => setPage('pallets')}>Графики</MobileNavButton>
        <MobileNavButton active={page === 'sensors'} icon="◉" onClick={() => setPage('sensors')}>Датчики</MobileNavButton>
        <MobileNavButton active={page === 'reading'} icon="⌁" onClick={() => setPage('reading')}>Считать</MobileNavButton>
      </nav>
    </div>
  )
}

function LogoutIcon() {
  return (
    <svg className="logout-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10 5H6.75A1.75 1.75 0 0 0 5 6.75v10.5C5 18.22 5.78 19 6.75 19H10" />
      <path d="M14.5 8.5 18 12l-3.5 3.5M18 12H9" />
    </svg>
  )
}

function NavButton({ children, icon, active, accent, onClick }: { children: string; icon: string; active: boolean; accent?: boolean; onClick: () => void }) {
  return <button className={`${active ? 'active' : ''} ${accent ? 'accent' : ''}`} aria-current={active ? 'page' : undefined} onClick={onClick}><span>{icon}</span>{children}</button>
}

function MobileNavButton({ children, icon, active, onClick }: { children: string; icon: string; active: boolean; onClick: () => void }) {
  return <button className={active ? 'active' : ''} aria-current={active ? 'page' : undefined} onClick={onClick}><span aria-hidden="true">{icon}</span><small>{children}</small></button>
}

function Loading() {
  return <section className="page-section loading"><div className="skeleton heading" /><div className="metric-grid">{Array.from({ length: 4 }, (_, index) => <div className="skeleton metric" key={index} />)}</div><div className="skeleton panel" /></section>
}

function ApiUnavailable({ message, retry }: { message: string; retry: () => void }) {
  return <section className="page-section"><div className="api-error"><span>!</span><p className="eyebrow">Backend недоступен</p><h1>Не удалось загрузить данные</h1><p>{message}</p><button className="button primary" onClick={retry}>Повторить</button><small>Для полного demo-сценария запустите `docker compose up --build`.</small></div></section>
}
